import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  HomeIcon,
  PlusCircleIcon,
  ClipboardDocumentListIcon,
  UsersIcon,
  CubeIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  SparklesIcon,
  WrenchScrewdriverIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  CreditCardIcon,
  BanknotesIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  XMarkIcon,
  QueueListIcon,
  CalculatorIcon,
  ArchiveBoxIcon,
} from '@heroicons/react/24/outline';
import { useUIStore } from '@/stores/uiStore';
import { useTenantStore } from '@/stores/tenantStore';
import { useAuthStore } from '@/stores/authStore';
import { useTicketStatusCounts } from '@/api/hooks/useTickets';
import { usePartStats } from '@/api/hooks/useParts';
import { usePickupQueue } from '@/api/hooks/usePackagePickup';
import { RepairStatus } from '@/api/types/common.types';
import { isNavKeyAllowed, type NavKey } from '@/utils/permissions';
import { useIsMobileNav } from '@/hooks/useMediaQuery';

// Grouped to match docs/mockups/repair_mockup_refined.html's sidebar sections
// (ראשי / מסחר / מערכת). Each item optionally carries a `countKey` naming which
// live count (see counts object below) should render as its trailing pill. `navKey` is checked
// against the current user's role via isNavKeyAllowed (see @/utils/permissions) - items the
// role can't access are filtered out below, not just visually hidden.
const NAV_GROUPS: {
  label: string;
  items: { name: string; href: string; icon: typeof HomeIcon; countKey?: 'tickets' | 'lowStock' | 'packagePickup'; navKey: NavKey }[];
}[] = [
  {
    label: 'ראשי',
    items: [
      { name: 'לוח בקרה', href: '/dashboard', icon: HomeIcon, navKey: 'dashboard' },
      { name: 'קליטה חדשה', href: '/intake', icon: PlusCircleIcon, navKey: 'intake' },
      { name: 'קריאות', href: '/tickets', icon: ClipboardDocumentListIcon, countKey: 'tickets', navKey: 'tickets' },
      { name: 'תור עבודה', href: '/queue', icon: QueueListIcon, navKey: 'queue' },
      { name: 'איסוף חבילות', href: '/package-pickup', icon: ArchiveBoxIcon, countKey: 'packagePickup', navKey: 'packagePickup' },
      { name: 'לקוחות', href: '/customers', icon: UsersIcon, navKey: 'customers' },
      { name: 'טכנאים', href: '/technicians', icon: WrenchScrewdriverIcon, navKey: 'technicians' },
      { name: 'מלאי', href: '/inventory', icon: CubeIcon, countKey: 'lowStock', navKey: 'inventory' },
    ],
  },
  {
    label: 'מסחר',
    items: [
      { name: 'מכירות', href: '/sales', icon: CurrencyDollarIcon, navKey: 'sales' },
      { name: 'קופה', href: '/cash', icon: CalculatorIcon, navKey: 'cash' },
      { name: 'חשבוניות', href: '/invoices', icon: DocumentTextIcon, navKey: 'invoices' },
      { name: 'תשלומים', href: '/payments', icon: CreditCardIcon, navKey: 'payments' },
      { name: 'עמלות', href: '/commissions', icon: BanknotesIcon, navKey: 'commissions' },
    ],
  },
  {
    label: 'מערכת',
    items: [
      { name: 'דוחות', href: '/reports', icon: ChartBarIcon, navKey: 'reports' },
      { name: 'משתמשים', href: '/users', icon: UserGroupIcon, navKey: 'users' },
      { name: 'הגדרות', href: '/settings', icon: Cog6ToothIcon, navKey: 'settings' },
    ],
  },
];

const ACTIVE_TICKET_STATUSES = [
  RepairStatus.Received,
  RepairStatus.InDiagnosis,
  RepairStatus.AwaitingApproval,
  RepairStatus.WaitingForParts,
  RepairStatus.InRepair,
  RepairStatus.ReadyForPickup,
];

// Which nav-group sections are collapsed - a plain localStorage-backed component state, not part
// of uiStore, since nothing else needs it and uiStore doesn't already have a persistence pattern
// to hook into. Only meaningful in the full (non icon-rail) sidebar - the icon-only rail has no
// group headers to collapse in the first place. Added 2026-09-07: with every role at full access
// (e.g. admin) the sidebar has ~18 nav items across 3 groups + AI, tall enough to force scrolling
// on a normal laptop screen - letting her collapse groups she uses less reclaims that space
// without hiding anything permanently.
const COLLAPSED_GROUPS_KEY = 'sidebarCollapsedGroups';

function readCollapsedGroups(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSED_GROUPS_KEY);
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch {
    return new Set();
  }
}

export function Sidebar() {
  const { sidebarOpen, sidebarCollapsed, setSidebarCollapsed, setSidebarOpen } = useUIStore();
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(readCollapsedGroups);

  const toggleGroup = (label: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      try {
        localStorage.setItem(COLLAPSED_GROUPS_KEY, JSON.stringify([...next]));
      } catch {
        // Private-browsing/storage-blocked - the toggle still works for this session via state.
      }
      return next;
    });
  };
  const { tenantInfo } = useTenantStore();
  const role = useAuthStore((s) => s.user?.role);
  const { data: statusCounts } = useTicketStatusCounts();
  const { data: partStats } = usePartStats();
  const { data: pickupQueue } = usePickupQueue();
  const isMobile = useIsMobileNav();
  const location = useLocation();

  // Icon-only collapse only makes sense as a desktop rail - a mobile drawer is always full width,
  // regardless of whatever sidebarCollapsed was left at from a previous desktop session.
  const collapsed = isMobile ? false : sidebarCollapsed;

  // Close on navigating to a new page - without this, picking a link from the mobile drawer would
  // leave the drawer covering the page it just navigated to. Skips its very first run: effects
  // fire on mount regardless of the dependency array, and closing on mount would fight whatever
  // sidebarOpen value the app deliberately started with (e.g. a user who just tapped the hamburger
  // open, if that ever happens to coincide with this component's own first mount).
  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    if (isMobile) setSidebarOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Close on Escape - standard expectation for any overlay/drawer.
  useEffect(() => {
    if (!isMobile || !sidebarOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isMobile, sidebarOpen, setSidebarOpen]);

  if (!sidebarOpen) return null;

  const counts: Record<'tickets' | 'lowStock' | 'packagePickup', number | undefined> = {
    tickets: statusCounts
      ? ACTIVE_TICKET_STATUSES.reduce((sum, s) => sum + ((statusCounts as Record<string, number>)[s] || 0), 0)
      : undefined,
    lowStock: partStats?.data?.lowStockParts,
    packagePickup: pickupQueue?.data?.length,
  };

  const visibleNavGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => isNavKeyAllowed(role, item.navKey)),
  })).filter((group) => group.items.length > 0);

  const showAiQuickIntake = isNavKeyAllowed(role, 'aiQuickIntake');
  const showAiDiagnosis = isNavKeyAllowed(role, 'aiDiagnosis');
  const showAiSection = showAiQuickIntake || showAiDiagnosis;

  return (
    <>
      {/* Backdrop - mobile drawer only. Desktop's rail never renders this (isMobile is false),
          so it can't accidentally darken the page on a wide screen. */}
      {isMobile && (
        <div
          className="fixed inset-0 z-30 bg-black/50 transition-opacity duration-300"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      <aside
        className={clsx(
          'fixed inset-y-0 z-40 flex flex-col text-white transition-all duration-300',
          isMobile ? 'w-[82vw] max-w-[300px] shadow-2xl' : collapsed ? 'w-70' : 'w-260'
        )}
      style={{
        right: 0,
        left: 'auto',
        background: 'linear-gradient(180deg, #148A8E 0%, #0A5560 92%)',
      }}
    >
      {/* Brand */}
      <div className={clsx(
        'flex items-center h-16 px-4 border-b border-white/10',
        collapsed ? 'justify-center' : 'justify-between'
      )}>
        {!collapsed && (
          <div className="flex items-center gap-3 animate-fade-in">
            <img
              src="/logo-white.png"
              alt="TCY"
              className="h-10 w-auto flex-shrink-0 object-contain"
              style={{ maxWidth: 96, mixBlendMode: 'screen' }}
            />
            <div className="font-extrabold text-[15px] leading-tight text-white">
              {tenantInfo?.name || 'TCY Repair'}
            </div>
          </div>
        )}
        {collapsed && (
          <img
            src="/logo-white.png"
            alt="TCY"
            className="w-10 h-10 object-contain"
            style={{ mixBlendMode: 'screen' }}
          />
        )}
      </div>

      {/* Toggle button - explicit inline position/size (not a Tailwind arbitrary class) so
          there's no ambiguity about what actually renders, and a solid brand-color circle (not
          white-on-white) so it's visible regardless of what's behind it. Positioned so its whole
          36px circle sits outside the sidebar's own box, straddling the boundary with the main
          content - the sidebar is docked to the right edge, so this button's "outer" side is its
          left. On mobile this is a close button for the drawer instead of the desktop
          collapse/expand toggle - icon-only collapse has no meaning in a drawer. */}
      <button
        onClick={() => (isMobile ? setSidebarOpen(false) : setSidebarCollapsed(!sidebarCollapsed))}
        style={{
          position: 'absolute',
          top: '80px',
          left: '-18px',
          width: '36px',
          height: '36px',
          borderRadius: '9999px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Match the sidebar's own gradient color at this point (background: 'linear-gradient(180deg,
          // #148A8E 0%, #0A5560 92%)' just above - #148A8E is the gradient's start/top shade).
          background: '#148A8E',
          border: '2px solid white',
          boxShadow: '0 2px 10px rgba(15,23,42,0.35)',
          cursor: 'pointer',
          zIndex: 50,
        }}
        aria-label={isMobile ? 'סגור תפריט צד' : collapsed ? 'הרחב תפריט צד' : 'כווץ תפריט צד'}
      >
        {isMobile ? (
          <XMarkIcon className="w-4 h-4 text-white" />
        ) : collapsed ? (
          <ChevronRightIcon className="w-4 h-4 text-white" />
        ) : (
          <ChevronLeftIcon className="w-4 h-4 text-white" />
        )}
      </button>

      {/* Navigation */}
      <nav className={clsx(
        'flex-1 py-2 overflow-y-auto overflow-x-hidden sidebar-nav-scroll',
        collapsed ? 'px-2' : 'px-3'
      )}>
        {visibleNavGroups.map((group) => {
          const isGroupCollapsed = !collapsed && collapsedGroups.has(group.label);
          return (
          <div key={group.label} className={clsx(collapsed && 'flex flex-col items-center')}>
            {!collapsed && (
              <button
                type="button"
                onClick={() => toggleGroup(group.label)}
                className="w-full flex items-center justify-between px-3 pt-4 pb-1.5 text-[10px] tracking-[.1em] text-[#7FB3AD] font-bold uppercase"
                aria-expanded={!isGroupCollapsed}
              >
                {group.label}
                <ChevronDownIcon
                  className={clsx('w-3 h-3 transition-transform duration-200', isGroupCollapsed && '-rotate-90')}
                />
              </button>
            )}
            {!isGroupCollapsed && (
            <div className={clsx('space-y-0.5', collapsed && 'flex flex-col items-center')}>
              {group.items.map((item) => {
                const count = item.countKey ? counts[item.countKey] : undefined;
                return (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={({ isActive }) =>
                      clsx(
                        'group relative flex items-center transition-all duration-200',
                        collapsed ? 'sidebar-icon-btn' : 'sidebar-link',
                        isActive && 'active'
                      )
                    }
                    aria-label={collapsed ? item.name : undefined}
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon className="flex-shrink-0 w-[18px] h-[18px]" />
                        {!collapsed && (
                          <>
                            <span className="ms-2.5 text-[13.5px]">{item.name}</span>
                            {!!count && (
                              <span
                                className={clsx(
                                  'ms-auto text-[10.5px] font-bold px-[7px] py-px rounded-full tabular-nums',
                                  isActive ? 'bg-primary-50 text-primary-700' : 'bg-white/15'
                                )}
                              >
                                {count}
                              </span>
                            )}
                          </>
                        )}

                        {collapsed && (
                          <div className="absolute left-full ms-2 px-2.5 py-1.5 bg-gray-900 rounded-lg text-white text-xs whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 pointer-events-none z-50 shadow-lg">
                            {item.name}
                            {!!count && <span className="ms-1.5 opacity-70">({count})</span>}
                          </div>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
            )}
          </div>
          );
        })}

        {/* AI Features section - available to every tenant regardless of PricingTier/Features as of
            2026-08-19 (business call, temporary) - role-based visibility via isNavKeyAllowed still
            applies. The Plan/Features mechanism itself (Tenant.cs, tenantStore.ts) is untouched and
            stays for future use; only the AI-specific hasFeature() checks were removed. */}
        {showAiSection && (
          <div className={clsx('mt-2 pt-3 border-t border-white/10', collapsed && 'flex flex-col items-center')}>
            {!collapsed && (
              <div className="text-[10px] tracking-[.1em] text-[#7FB3AD] font-bold uppercase px-3 pb-1.5">
                AI
              </div>
            )}
            {showAiQuickIntake && (
              <NavLink
                to="/ai/quick-intake"
                className={({ isActive }) =>
                  clsx(
                    'group relative flex items-center transition-all duration-200',
                    collapsed ? 'sidebar-icon-btn mt-0.5' : 'sidebar-link',
                    isActive && 'active'
                  )
                }
              >
                {() => (
                  <>
                    <SparklesIcon className="w-[18px] h-[18px] flex-shrink-0" />
                    {!collapsed && <span className="ms-2.5 text-[13.5px]">קליטה מהירה AI</span>}
                    {collapsed && (
                      <div className="absolute left-full ms-2 px-2.5 py-1.5 bg-gray-900 rounded-lg text-white text-xs whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 pointer-events-none z-50 shadow-lg">
                        קליטה מהירה AI
                      </div>
                    )}
                  </>
                )}
              </NavLink>
            )}
            {showAiDiagnosis && (
              <NavLink
                to="/ai/diagnosis"
                className={({ isActive }) =>
                  clsx(
                    'group relative flex items-center transition-all duration-200',
                    collapsed ? 'sidebar-icon-btn mt-0.5' : 'sidebar-link',
                    isActive && 'active'
                  )
                }
              >
                {() => (
                  <>
                    <SparklesIcon className="w-[18px] h-[18px] flex-shrink-0" />
                    {!collapsed && <span className="ms-2.5 text-[13.5px]">אבחון AI</span>}
                    {collapsed && (
                      <div className="absolute left-full ms-2 px-2.5 py-1.5 bg-gray-900 rounded-lg text-white text-xs whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 pointer-events-none z-50 shadow-lg">
                        אבחון AI
                      </div>
                    )}
                  </>
                )}
              </NavLink>
            )}
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className={clsx('px-5 py-3.5 border-t border-white/10', collapsed && 'flex justify-center px-3')}>
        {!collapsed ? (
          <div className="text-[10.5px] text-[#8FC4BF]">
            <div className="font-bold text-[#C7E6E2]">TCY Repair System v2.0</div>
            <div className="inline-flex items-center gap-1.5 mt-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              מחובר
            </div>
          </div>
        ) : (
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
        )}
      </div>
      </aside>
    </>
  );
}
